import React, { useMemo, useState } from "react";
import PageWrapper from "components/PageWrapper";
import {
  Truck,
  Package,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Search,
  Calendar,
  ChevronDown,
  Eye,
  ExternalLink,
  ArrowRight,
  Download,
} from "lucide-react";

const CARRIER_META = {
  DHL: { short: "DHL", badge: "bg-[#FFCC00] text-[#D40511]", service: "Express International" },
  FedEx: { short: "FedEx", badge: "bg-[#4D148C] text-white", service: "International Priority" },
  UPS: { short: "UPS", badge: "bg-[#351C15] text-[#FFB500]", service: "Worldwide Saver" },
  USPS: { short: "USPS", badge: "bg-[#004B87] text-white", service: "Priority Mail Express" },
};

const DIST_SHORT = { mouser: "Mou", digikey: "DK", element14: "E14", tme: "TME", arrow: "Arr" };
const DIST_WM = {
  mouser: "bg-[#005DAA]",
  digikey: "bg-[#CC0000]",
  element14: "bg-[#FF6600]",
  arrow: "bg-[#0B7D72]",
  tme: "bg-[#E2001A]",
};

const MILESTONES = ["Order placed", "Picked & packed", "In transit", "Delivered"];
const STATUS_STEP = {
  pending: 0,
  "in-transit": 2,
  "out-for-delivery": 2,
  delayed: 2,
  delivered: 4,
};

const STATUS_META = {
  pending: { label: "Pending", pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  "in-transit": { label: "In transit", pill: "bg-s360-info-bg text-[#002E4D]", dot: "bg-s360-info" },
  "out-for-delivery": {
    label: "Out for delivery",
    pill: "bg-s360-warning-bg text-s360-warning-ink",
    dot: "bg-s360-warning",
  },
  delivered: { label: "Delivered", pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  delayed: { label: "Delayed", pill: "bg-s360-danger-bg text-[#700A14]", dot: "bg-s360-danger" },
};

const ALL_SHIPMENTS = [
  {
    id: "s1",
    tracking: "1Z999AA1 0123 4567 89",
    carrier: "UPS",
    order: "ORD-2026-0142",
    supplier: "Mouser",
    api_partner: "mouser",
    origin: { city: "Mansfield, TX", country: "US" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "in-transit",
    eta: "May 27, 2026",
    etaTone: "soon",
    weight: "8.4 kg",
    pieces: 3,
    events: [
      { title: "Order placed", loc: "Mouser Electronics · Mansfield, TX", when: "May 22 · 14:30", done: true },
      { title: "Picked up by UPS", loc: "Mansfield, TX Distribution Center", when: "May 22 · 18:42", done: true },
      { title: "Arrived at hub", loc: "Louisville, KY", when: "May 23 · 02:15", done: true },
      { title: "Departed hub", loc: "Louisville, KY → Oakland, CA", when: "May 24 · 04:08", done: true },
      { title: "In transit", loc: "Oakland, CA Distribution Center", when: "May 25 · 21:30", done: false, current: true },
      { title: "Out for delivery", loc: "San Jose, CA", when: "—", done: false },
      { title: "Delivered", loc: "San Jose, CA", when: "—", done: false },
    ],
  },
  {
    id: "s2",
    tracking: "1234 5678 9012",
    carrier: "FedEx",
    order: "ORD-2026-0134",
    supplier: "Arrow",
    api_partner: "arrow",
    origin: { city: "Reno, NV", country: "US" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "out-for-delivery",
    eta: "May 26, 2026",
    etaTone: "soon",
    weight: "2.1 kg",
    pieces: 1,
    events: [],
  },
  {
    id: "s3",
    tracking: "EE 1234 5678 9DE",
    carrier: "DHL",
    order: "ORD-2026-0138",
    supplier: "Element 14",
    api_partner: "element14",
    origin: { city: "Leeds, UK", country: "GB" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "delivered",
    eta: "May 24, 2026",
    etaTone: "",
    weight: "1.8 kg",
    pieces: 2,
    events: [],
  },
  {
    id: "s4",
    tracking: "1Z999AA1 0123 4598 76",
    carrier: "UPS",
    order: "ORD-2026-0141",
    supplier: "Digi-Key",
    api_partner: "digikey",
    origin: { city: "Thief River Falls, MN", country: "US" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "delayed",
    eta: "May 24, 2026",
    etaTone: "late",
    weight: "5.6 kg",
    pieces: 2,
    events: [],
  },
  {
    id: "s5",
    tracking: "9400 1118 9923 6789 0123 45",
    carrier: "USPS",
    order: "ORD-2026-0131",
    supplier: "TME",
    api_partner: "tme",
    origin: { city: "Łódź, PL", country: "PL" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "in-transit",
    eta: "Jun 02, 2026",
    etaTone: "",
    weight: "0.9 kg",
    pieces: 1,
    events: [],
  },
  {
    id: "s6",
    tracking: "1234 5678 9034",
    carrier: "FedEx",
    order: "ORD-2026-0128",
    supplier: "Digi-Key",
    api_partner: "digikey",
    origin: { city: "Thief River Falls, MN", country: "US" },
    dest: { city: "San Jose, CA", country: "US" },
    status: "delivered",
    eta: "May 20, 2026",
    etaTone: "",
    weight: "12.4 kg",
    pieces: 4,
    events: [],
  },
];

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${meta.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const Milestone = ({ status }) => {
  const step = STATUS_STEP[status] ?? 0;
  const isDelivered = status === "delivered";
  return (
    <div className="flex flex-col gap-1.5 min-w-[140px]">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => {
          let cls = "bg-s360-bg3";
          if (isDelivered || i < step) cls = "bg-s360-success";
          else if (i === step && status !== "delayed") cls = "bg-s360-violet";
          return <span key={i} className={`h-1.5 flex-1 rounded-full ${cls}`} />;
        })}
      </div>
      <span className="text-[11px] text-s360-fg3">
        {isDelivered ? (
          <strong className="text-s360-success">Delivered</strong>
        ) : status === "out-for-delivery" ? (
          <>
            <strong className="text-s360-fg1">Out for delivery</strong> · last mile
          </>
        ) : status === "delayed" ? (
          <strong className="text-s360-danger">Delayed in transit</strong>
        ) : (
          <>
            <strong className="text-s360-fg1">{MILESTONES[step]}</strong> · step {step + 1} of 4
          </>
        )}
      </span>
    </div>
  );
};

const ShipmentTimeline = ({ shipment }) => {
  const events =
    shipment.events.length > 0
      ? shipment.events
      : [
          { title: "Order placed", loc: `${shipment.supplier} · ${shipment.origin.city}`, when: "May 22 · 14:30", done: true },
          { title: "Picked & packed", loc: shipment.origin.city, when: "May 23 · 09:14", done: shipment.status !== "pending" },
          {
            title: "In transit",
            loc: `${shipment.origin.city} → ${shipment.dest.city}`,
            when: "May 24 · 04:08",
            done: shipment.status === "delivered" || shipment.status === "out-for-delivery",
            current: shipment.status === "in-transit" || shipment.status === "delayed",
          },
          {
            title: "Delivered",
            loc: shipment.dest.city,
            when: shipment.status === "delivered" ? shipment.eta : "—",
            done: shipment.status === "delivered",
            current: shipment.status === "out-for-delivery",
          },
        ];

  const kv = [
    ["Tracking #", shipment.tracking],
    ["Carrier", `${shipment.carrier} · ${CARRIER_META[shipment.carrier]?.service}`],
    ["Order", shipment.order],
    ["Supplier", shipment.supplier],
    ["Origin", shipment.origin.city],
    ["Destination", shipment.dest.city],
    ["Pieces · Weight", `${shipment.pieces} pkg · ${shipment.weight}`],
    ["ETA", shipment.eta],
  ];

  return (
    <td colSpan={8} className="p-0">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 bg-s360-stripe border-t border-s360-stroke1 p-5">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-s360-fg3 mb-3">
            Tracking events
          </h4>
          <div className="flex flex-col">
            {events.map((e, i) => (
              <div key={i} className="flex items-start gap-3 pb-3 last:pb-0">
                <span className="flex flex-col items-center self-stretch">
                  <span
                    className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${
                      e.current ? "bg-s360-violet ring-4 ring-s360-violet-50" : e.done ? "bg-s360-success" : "bg-s360-bg3"
                    }`}
                  />
                  {i < events.length - 1 && <span className="w-px flex-1 bg-s360-stroke1 my-1" />}
                </span>
                <div className="flex-1 flex justify-between gap-3 min-w-0">
                  <div className="flex flex-col min-w-0">
                    <span
                      className={`text-[13px] font-semibold ${
                        e.current ? "text-s360-violet" : e.done ? "text-s360-fg1" : "text-s360-fg3"
                      }`}
                    >
                      {e.title}
                    </span>
                    <span className="text-xs text-s360-fg3 truncate">{e.loc}</span>
                  </div>
                  <span className="text-xs text-s360-fg3 whitespace-nowrap">{e.when}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-s360-fg3 mb-3">
            Shipment details
          </h4>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
            {kv.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-[11px] text-s360-fg3">{k}</dt>
                <dd className="text-[13px] font-semibold text-s360-fg1 text-right break-words">{v}</dd>
              </React.Fragment>
            ))}
          </dl>
          <div className="flex flex-col gap-2 mt-4">
            <button className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg1 hover:bg-s360-bg2">
              <ExternalLink className="w-3 h-3" /> Open with {shipment.carrier}
            </button>
            <button className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg1 hover:bg-s360-bg2">
              <Download className="w-3 h-3" /> Download POD
            </button>
          </div>
        </div>
      </div>
    </td>
  );
};

const SummaryTile = ({ tone, Icon, label, value }) => {
  const bubble = {
    violet: "bg-s360-violet-50 text-s360-violet",
    info: "bg-s360-info-bg text-s360-info",
    amber: "bg-s360-warning-bg text-s360-warning",
    danger: "bg-s360-danger-bg text-s360-danger",
    green: "bg-s360-success-bg text-s360-success",
  }[tone];
  return (
    <div className="bg-white rounded-md shadow-sm px-4 py-3.5 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-lg inline-flex items-center justify-center shrink-0 ${bubble}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex flex-col">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
          {label}
        </span>
        <span className="text-[22px] leading-7 font-bold text-s360-ink tabular-nums">{value}</span>
      </div>
    </div>
  );
};

const ShipmentPage = () => {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const counts = useMemo(
    () => ({
      all: ALL_SHIPMENTS.length,
      "in-transit": ALL_SHIPMENTS.filter((s) => s.status === "in-transit").length,
      "out-for-delivery": ALL_SHIPMENTS.filter((s) => s.status === "out-for-delivery").length,
      delivered: ALL_SHIPMENTS.filter((s) => s.status === "delivered").length,
      delayed: ALL_SHIPMENTS.filter((s) => s.status === "delayed").length,
    }),
    [],
  );

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return ALL_SHIPMENTS.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (
        qq &&
        !s.tracking.toLowerCase().includes(qq) &&
        !s.order.toLowerCase().includes(qq) &&
        !s.supplier.toLowerCase().includes(qq)
      )
        return false;
      return true;
    });
  }, [filter, q]);

  const FILTER_TABS = [
    { key: "all", label: "All" },
    { key: "in-transit", label: "In transit" },
    { key: "out-for-delivery", label: "Out for delivery" },
    { key: "delayed", label: "Delayed" },
    { key: "delivered", label: "Delivered" },
  ];

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Shipments
          </h1>
          <span className="text-sm text-s360-fg3">
            Real-time tracking across UPS, FedEx, DHL, and USPS — all your distributors in one place.
          </span>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <SummaryTile tone="violet" Icon={Package} label="Total" value={ALL_SHIPMENTS.length} />
          <SummaryTile tone="info" Icon={Truck} label="In transit" value={counts["in-transit"]} />
          <SummaryTile tone="amber" Icon={Clock} label="Out for delivery" value={counts["out-for-delivery"]} />
          <SummaryTile tone="danger" Icon={AlertTriangle} label="Delayed" value={counts.delayed} />
          <SummaryTile tone="green" Icon={CheckCircle2} label="Delivered" value={counts.delivered} />
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="inline-flex bg-white border border-s360-stroke1 rounded-md overflow-hidden">
            {FILTER_TABS.map((tab, i) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`h-[34px] px-3 inline-flex items-center gap-1.5 text-xs font-semibold ${
                  i > 0 ? "border-l border-s360-stroke1" : ""
                } ${
                  filter === tab.key
                    ? "bg-s360-violet-50 text-s360-violet-900"
                    : "text-s360-fg2 hover:bg-s360-bg2"
                }`}
              >
                {tab.label}
                <span
                  className={`font-mono text-[10px] px-1.5 py-px rounded-sm ${
                    filter === tab.key ? "bg-s360-violet-100" : "bg-black/[0.06]"
                  }`}
                >
                  {counts[tab.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 h-9 px-3 bg-white border border-s360-stroke1 border-b-2 rounded-md min-w-[280px] flex-1 focus-within:border-s360-violet text-s360-fg3 focus-within:text-s360-fg1">
            <Search className="w-3.5 h-3.5" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by tracking #, order ID, or supplier…"
              className="flex-1 bg-transparent outline-none text-sm text-s360-fg1 placeholder:text-s360-fg-disabled"
            />
          </div>
          <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg2 hover:bg-s360-bg2">
            <Calendar className="w-3.5 h-3.5" /> Last 30 days <ChevronDown className="w-3 h-3" />
          </button>
          <span className="ml-auto text-xs text-s360-fg3">
            <strong className="text-s360-fg1">{rows.length}</strong> of {ALL_SHIPMENTS.length} shipments
          </span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[980px]">
              <thead>
                <tr>
                  {["Tracking #", "Carrier", "Supplier", "Route", "Progress", "Status", "ETA", "Actions"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const isExp = expandedId === s.id;
                  const carrier = CARRIER_META[s.carrier];
                  const etaCls =
                    s.etaTone === "late"
                      ? "text-s360-danger font-semibold"
                      : s.etaTone === "soon"
                      ? "text-s360-warning font-semibold"
                      : "text-s360-fg2";
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        className={`border-b border-s360-bg2 cursor-pointer ${
                          isExp ? "bg-s360-violet-50" : "hover:bg-s360-stripe"
                        }`}
                        onClick={() => setExpandedId(isExp ? null : s.id)}
                      >
                        <td className="py-3 px-3.5">
                          <div className="flex flex-col">
                            <span className="font-mono text-[13px] font-semibold text-s360-fg1">
                              {s.tracking}
                            </span>
                            <span className="font-mono text-[11px] text-s360-fg3">{s.order}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3.5">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`px-1.5 h-[22px] rounded-[4px] inline-flex items-center justify-center text-[10px] font-bold ${carrier?.badge}`}
                            >
                              {carrier?.short}
                            </span>
                            <span className="flex flex-col">
                              <span className="text-[13px] font-semibold text-s360-fg1">{s.carrier}</span>
                              <span className="text-[11px] text-s360-fg3">{carrier?.service}</span>
                            </span>
                          </span>
                        </td>
                        <td className="py-3 px-3.5">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`w-[26px] h-[26px] rounded-[5px] inline-flex items-center justify-center text-[10px] font-bold text-white ${
                                DIST_WM[s.api_partner] || "bg-gray-400"
                              }`}
                            >
                              {DIST_SHORT[s.api_partner] || s.supplier.slice(0, 3)}
                            </span>
                            <span className="text-[13px] text-s360-fg1">{s.supplier}</span>
                          </span>
                        </td>
                        <td className="py-3 px-3.5">
                          <span className="inline-flex items-center gap-1.5 text-[13px] text-s360-fg2">
                            <span className="inline-flex items-baseline gap-0.5">
                              {s.origin.city.split(",")[0]}
                              <small className="text-[10px] text-s360-fg3">{s.origin.country}</small>
                            </span>
                            <ArrowRight className="w-3 h-3 text-s360-fg3" />
                            <span className="inline-flex items-baseline gap-0.5">
                              {s.dest.city.split(",")[0]}
                              <small className="text-[10px] text-s360-fg3">{s.dest.country}</small>
                            </span>
                          </span>
                        </td>
                        <td className="py-3 px-3.5">
                          <Milestone status={s.status} />
                        </td>
                        <td className="py-3 px-3.5">
                          <StatusPill status={s.status} />
                        </td>
                        <td className="py-3 px-3.5">
                          <span className={`font-mono text-xs tabular-nums ${etaCls}`}>{s.eta}</span>
                        </td>
                        <td className="py-3 px-3.5" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() => setExpandedId(isExp ? null : s.id)}
                              title={isExp ? "Hide timeline" : "Show timeline"}
                              className="w-[30px] h-[30px] rounded-[5px] inline-flex items-center justify-center border border-s360-stroke1 text-s360-fg2 hover:bg-s360-bg2"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              title="Open with carrier"
                              className="w-[30px] h-[30px] rounded-[5px] inline-flex items-center justify-center border border-s360-stroke1 text-s360-fg2 hover:bg-s360-bg2"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExp && (
                        <tr>
                          <ShipmentTimeline shipment={s} />
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-s360-fg3">
                      No shipments match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ShipmentPage;
